/**
 * Quotations page — optional financial documents for high-value treatments.
 * Living inside `/app/billing/quotations`. Reuses the invoice-table visual
 * language so clinics recognise the paradigm instantly.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Plus, Search, ChevronDown, ChevronUp, Filter } from "lucide-react";
import BillingTabs from "@/components/app/BillingTabs";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  MoreHorizontal, Eye, Send, Download, Printer, Copy, XCircle,
  Trash2, MessageCircle, Mail, ArrowRightCircle, CheckCircle2, Ban,
} from "lucide-react";
import QuotationFormDrawer from "@/components/billing/QuotationFormDrawer";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", {
  day: "numeric", month: "short", year: "numeric",
}) : "—";

const STATUS_META = {
  draft:     { label: "Draft",     cls: "bg-[#EFEEEA] text-[#6E6D69] border-[#DDDBD3]" },
  sent:      { label: "Sent",      cls: "bg-[#EEF0FA] text-[#3C4A8A] border-[#C7CFEB]" },
  approved:  { label: "Approved",  cls: "bg-[#E8F0EB] text-[#2D6A4F] border-[#B8D6C2]" },
  rejected:  { label: "Rejected",  cls: "bg-[#FCECEC] text-[#B94A48] border-[#F0B0AE]" },
  expired:   { label: "Expired",   cls: "bg-[#FFF4E5] text-[#8A4B00] border-[#F2C494]" },
};

const STATUS_FILTERS = [
  { id: "all",      label: "All" },
  { id: "draft",    label: "Draft" },
  { id: "sent",     label: "Sent" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "expired",  label: "Expired" },
];

const SORT_KEYS = {
  date_desc:    { label: "Newest first",       key: "created_at",   dir: -1 },
  date_asc:     { label: "Oldest first",       key: "created_at",   dir:  1 },
  amount_desc:  { label: "Amount: high → low", key: "total",        dir: -1 },
  amount_asc:   { label: "Amount: low → high", key: "total",        dir:  1 },
  patient_asc:  { label: "Patient A → Z",      key: "patient_name", dir:  1, str: true },
  validity_asc: { label: "Valid until — soonest first", key: "valid_until", dir: 1, str: true },
};


export default function Quotations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sortId, setSortId] = useState("date_desc");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const canDelete = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotations");
      setItems(data || []);
    } catch (e) {
      toast.error(apiError(e, "Could not load quotations"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = items.filter((i) => {
      if (status !== "all" && i.status !== status) return false;
      if (!needle) return true;
      const hay = [
        i.quotation_number, i.patient_name, i.patient_phone, i.doctor_name,
        i.treatment_summary?.primary,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
    const s = SORT_KEYS[sortId];
    rows.sort((a, b) => {
      const av = a[s.key]; const bv = b[s.key];
      if (s.str) return s.dir * String(av || "").localeCompare(String(bv || ""));
      return s.dir * ((Number(av) || 0) - (Number(bv) || 0));
    });
    return rows;
  }, [items, q, status, sortId]);

  const kpis = useMemo(() => {
    const draft = items.filter(i => i.status === "draft").length;
    const sent = items.filter(i => i.status === "sent").length;
    const approved = items.filter(i => i.status === "approved").length;
    const pipeline = items
      .filter(i => ["draft", "sent"].includes(i.status))
      .reduce((s, i) => s + (i.total || 0), 0);
    return { draft, sent, approved, pipeline };
  }, [items]);

  const doAction = async (fn, msg) => {
    const tid = toast.loading(msg + "…");
    try { await fn(); toast.success(msg + " ✓", { id: tid }); load(); }
    catch (err) { toast.error(apiError(err, msg + " failed"), { id: tid }); }
  };

  const onSend = (qq) => doAction(
    () => api.post(`/quotations/${qq.id}/send`, { channels: [] }),
    "Marked as sent",
  );
  const onApprove = (qq) => doAction(
    () => api.post(`/quotations/${qq.id}/approve`),
    "Approved",
  );
  const onReject = (qq) => {
    const reason = window.prompt("Reason for rejection? (optional)");
    return doAction(
      () => api.post(`/quotations/${qq.id}/reject`, { reason }),
      "Rejected",
    );
  };
  const onDuplicate = (qq) => doAction(
    () => api.post(`/quotations/${qq.id}/duplicate`),
    "Duplicated",
  );
  const onDelete = (qq) => {
    if (!window.confirm(`Delete quotation ${qq.quotation_number}?`)) return;
    return doAction(
      () => api.delete(`/quotations/${qq.id}`),
      "Deleted",
    );
  };
  const onConvert = (qq) => {
    if (!window.confirm(`Convert ${qq.quotation_number} into an invoice for ₹${Number(qq.total).toLocaleString("en-IN")}?`)) return;
    return doAction(
      () => api.post(`/quotations/${qq.id}/convert-to-invoice`).then((r) => {
        toast.success(`Invoice ${r.data.invoice.invoice_number} created`);
      }),
      "Converting",
    );
  };
  const onDownloadPdf = async (qq) => {
    try {
      const resp = await api.get(`/quotations/${qq.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `quotation-${qq.quotation_number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("PDF download failed"); }
  };
  const onWhatsapp = async (qq) => {
    const tid = toast.loading("Sending on WhatsApp…");
    try {
      await api.post(`/quotations/${qq.id}/send`, { channels: ["whatsapp"] });
      toast.success("Sent on WhatsApp", { id: tid });
      load();
    } catch (err) { toast.error(apiError(err, "WhatsApp send failed"), { id: tid }); }
  };
  const onEmail = async (qq) => {
    const to = window.prompt(`Send quotation ${qq.quotation_number} to which email?`, "");
    if (!to) return;
    const tid = toast.loading("Sending email…");
    try {
      await api.post(`/quotations/${qq.id}/send`, { channels: ["email"], to_email: to });
      toast.success(`Emailed to ${to}`, { id: tid });
      load();
    } catch (err) { toast.error(apiError(err, "Email failed"), { id: tid }); }
  };

  return (
    <div className="space-y-4" data-testid="quotations-page">
      <BillingTabs />
      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline text-ink-soft">Quotations</div>
          <div className="font-display text-3xl text-ink mt-1">Estimates that convert.</div>
        </div>
        <button
          data-testid="new-quotation-btn"
          onClick={() => setCreating(true)}
          className="btn-primary inline-flex items-center gap-2 py-2.5"
        >
          <Plus className="w-4 h-4" /> New Quotation
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Draft" value={kpis.draft} testid="kpi-draft" />
        <KpiCard label="Sent" value={kpis.sent} testid="kpi-sent" />
        <KpiCard label="Approved" value={kpis.approved} testid="kpi-approved" tone="pine" />
        <KpiCard label="Pipeline value" value={INR(kpis.pipeline)} testid="kpi-pipeline" tone="clay" />
      </div>

      {/* Toolbar + table */}
      <div className="rounded-3xl bg-white border border-[#EFEADC] overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-[#F0EBE0]">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="overline text-ink-soft">All quotations</div>
              <div className="font-display text-2xl text-ink mt-1">{filtered.length} quotation{filtered.length === 1 ? "" : "s"}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  data-testid="qt-search"
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search quotation, patient, phone, doctor…"
                  className="pl-9 pr-3 py-2 rounded-full border border-[#E8EAE9] text-xs bg-white w-72"
                />
              </div>
              <SortDropdown sortId={sortId} setSortId={setSortId} />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-1.5 flex-wrap" data-testid="qt-status-tabs">
            {STATUS_FILTERS.map((s) => {
              const count = s.id === "all"
                ? items.length
                : items.filter((i) => i.status === s.id).length;
              const active = status === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`qt-status-tab-${s.id}`}
                  onClick={() => setStatus(s.id)}
                  className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                    active ? "bg-pine text-linen" : "bg-white border border-[#E8EAE9] text-ink-mid hover:bg-linen/70"
                  }`}
                >
                  {s.label}
                  <span className={`text-[10px] ${active ? "text-linen/70" : "text-ink-soft"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="qt-table">
            <thead className="sticky top-0 bg-white z-[1]">
              <tr className="text-left text-ink-soft overline">
                <th className="px-5 py-3 font-normal w-[14%]">Quotation</th>
                <th className="px-3 py-3 font-normal w-[15%]">Patient</th>
                <th className="px-3 py-3 font-normal w-[15%]">Doctor</th>
                <th className="px-3 py-3 font-normal w-[18%]">Treatment</th>
                <th className="px-3 py-3 font-normal text-right w-[12%]">Amount</th>
                <th className="px-3 py-3 font-normal w-[13%]">Valid Until</th>
                <th className="px-3 py-3 font-normal w-[8%]">Status</th>
                <th className="px-3 py-3 font-normal text-right w-[5%]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EEE4]">
              {loading && (
                <tr><td colSpan={8} className="py-10 text-center text-ink-soft text-sm">Loading…</td></tr>
              )}
              {!loading && filtered.map((qq) => (
                <QuotationRow
                  key={qq.id} qq={qq} canDelete={canDelete}
                  onOpen={() => setEditingId(qq.id)}
                  onSend={onSend} onApprove={onApprove} onReject={onReject}
                  onDuplicate={onDuplicate} onDelete={onDelete}
                  onConvert={onConvert} onDownloadPdf={onDownloadPdf}
                  onWhatsapp={onWhatsapp} onEmail={onEmail}
                />
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-ink-soft text-sm">
                  {items.length === 0
                    ? "No quotations yet. Create your first quotation for a high-value treatment."
                    : "No quotations match your filters."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <QuotationFormDrawer
        open={creating || !!editingId}
        quotationId={editingId}
        onOpenChange={(v) => { if (!v) { setCreating(false); setEditingId(null); } }}
        onSaved={() => { setCreating(false); setEditingId(null); load(); }}
      />
    </div>
  );
}


/* ==================================================================== */
/* Row + primitives                                                     */
/* ==================================================================== */
function QuotationRow({ qq, canDelete, onOpen, onSend, onApprove, onReject,
                       onDuplicate, onDelete, onConvert, onDownloadPdf,
                       onWhatsapp, onEmail }) {
  const t = qq.treatment_summary || {};
  const status = qq.status;
  const isEditable = ["draft"].includes(status);
  const canSend = ["draft", "sent"].includes(status);
  const canApproveReject = ["sent"].includes(status);
  const canConvert = status === "approved" && !qq.converted_invoice_id;

  return (
    <tr className="hover:bg-linen/40 transition-colors" data-testid={`qt-row-${qq.id}`}>
      <td className="px-5 py-4 align-top">
        <button onClick={onOpen} className="text-left group" data-testid={`qt-open-${qq.id}`}>
          <div className="font-mono text-[11px] text-ink group-hover:text-pine transition-colors">{qq.quotation_number}</div>
          <div className="text-[10px] text-ink-soft mt-1">{fmtDate(qq.created_at)}</div>
        </button>
      </td>
      <td className="px-3 py-4 align-top">
        <div className="truncate max-w-[160px]">
          <div className="text-ink font-medium truncate">{qq.patient_name}</div>
          <div className="text-[10px] text-ink-soft mt-0.5 font-mono truncate">{qq.patient_phone || "—"}</div>
        </div>
      </td>
      <td className="px-3 py-4 align-top text-ink text-sm truncate max-w-[160px]">
        {qq.doctor_name || <span className="text-ink-soft">Unassigned</span>}
      </td>
      <td className="px-3 py-4 align-top">
        {t.all?.length > 1 ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-left group max-w-[220px]" data-testid={`qt-treatment-${qq.id}`}>
                <div className="text-ink truncate">{t.primary}</div>
                <div className="text-[10px] text-ink-soft mt-0.5 inline-flex items-center gap-1">
                  {t.tooth && <span>Tooth {t.tooth} · </span>}
                  <span className="text-pine group-hover:underline">+{t.more} more</span>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2">
              <div className="overline text-ink-soft px-2 pb-1.5">All treatments</div>
              {t.all.map((x, i) => (
                <div key={i} className="px-2 py-1.5 text-xs text-ink border-b border-[#F5F1E5] last:border-0">
                  {x.procedure}{x.tooth ? ` · Tooth ${x.tooth}` : ""}
                </div>
              ))}
            </PopoverContent>
          </Popover>
        ) : (
          <div className="max-w-[220px]">
            <div className="text-ink truncate">{t.primary || "—"}</div>
            {t.tooth && <div className="text-[10px] text-ink-soft mt-0.5">Tooth {t.tooth}</div>}
          </div>
        )}
      </td>
      <td className="px-3 py-4 align-top text-right font-display text-lg tabular-nums text-ink">
        {INR(qq.total)}
      </td>
      <td className="px-3 py-4 align-top text-sm text-ink-mid whitespace-nowrap">
        {fmtDate(qq.valid_until)}
      </td>
      <td className="px-3 py-4 align-top">
        <StatusPill status={status} testid={`qt-status-${qq.id}`} />
        {qq.converted_invoice_id && (
          <div className="text-[10px] text-ink-soft mt-1 font-mono">Invoiced</div>
        )}
      </td>
      <td className="px-3 py-4 align-top">
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={onOpen}
            data-testid={`qt-action-view-${qq.id}`}
            title="View / edit"
            className="w-8 h-8 rounded-full grid place-items-center text-ink-mid hover:bg-linen border border-[#EFEADC]"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`qt-action-more-${qq.id}`}
                title="More"
                className="w-8 h-8 rounded-full grid place-items-center text-ink-mid hover:bg-linen border border-[#EFEADC]"
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid={`qt-more-menu-${qq.id}`}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-ink-soft">Send</DropdownMenuLabel>
              {canSend && (
                <DropdownMenuItem onClick={() => onSend(qq)} data-testid={`qt-more-send-${qq.id}`}>
                  <Send className="w-3.5 h-3.5" /> Mark as sent
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onWhatsapp(qq)} data-testid={`qt-more-whatsapp-${qq.id}`}>
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEmail(qq)} data-testid={`qt-more-email-${qq.id}`}>
                <Mail className="w-3.5 h-3.5" /> Email
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-ink-soft">Documents</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onDownloadPdf(qq)} data-testid={`qt-more-pdf-${qq.id}`}>
                <Download className="w-3.5 h-3.5" /> Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDownloadPdf(qq)}>
                <Printer className="w-3.5 h-3.5" /> Print
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {canApproveReject && (
                <>
                  <DropdownMenuItem onClick={() => onApprove(qq)} data-testid={`qt-more-approve-${qq.id}`} className="text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onReject(qq)} data-testid={`qt-more-reject-${qq.id}`} className="text-clay">
                    <Ban className="w-3.5 h-3.5" /> Reject
                  </DropdownMenuItem>
                </>
              )}
              {canConvert && (
                <DropdownMenuItem onClick={() => onConvert(qq)} data-testid={`qt-more-convert-${qq.id}`} className="text-pine font-medium">
                  <ArrowRightCircle className="w-3.5 h-3.5" /> Convert to invoice
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onDuplicate(qq)}>
                <Copy className="w-3.5 h-3.5" /> Duplicate
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(qq)}
                  className="text-red-600"
                  data-testid={`qt-more-delete-${qq.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

function KpiCard({ label, value, tone, testid }) {
  const cls =
    tone === "pine" ? "text-[#2D6A4F]" :
    tone === "clay" ? "text-clay" : "text-ink";
  return (
    <div className="rounded-2xl bg-white border border-[#EFEADC] p-4" data-testid={testid}>
      <div className="overline text-ink-soft">{label}</div>
      <div className={`font-display text-2xl mt-1 tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status, testid }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span
      data-testid={testid}
      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-medium ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

function SortDropdown({ sortId, setSortId }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="qt-sort"
          className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] bg-white text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
        >
          <ChevronUp className="w-3 h-3" /><ChevronDown className="w-3 h-3 -ml-2.5" />
          {SORT_KEYS[sortId].label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {Object.entries(SORT_KEYS).map(([k, v]) => (
          <DropdownMenuItem key={k} onClick={() => setSortId(k)}>
            {v.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
